import { DashboardCard } from "@/components/organisms/DashboardCard/DashboardCard";
import { useTabManagement } from "@/overlay/overlays/default/NavigationHeader/hooks/useTabManagement";
import { useProfile } from "@/reducer/ProfileState";
import type { AiChatMessage } from "@/types/ai-chat";
import { t } from "@lingui/core/macro";
import type { SlimProfile } from "@mm/metx-workbench.meteomatics.com";
import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

type WorkspaceLink = {
  profileId: number;
  tabId: number;
};

const WORKSPACE_PATH_REGEX = /\/(?:beta\/|ncm\/)?workspace\/(\d+)\/(\d+)/;
const RAW_WORKSPACE_LINK_REGEX = /(?:https?:\/\/[^\s)]+)?\/(?:beta\/|ncm\/)?workspace\/\d+\/\d+/;

const normalizeContent = (value: string) => value.trim();

export const extractWorkspaceLink = (content: string): { link: WorkspaceLink | null; displayContent: string } => {
  const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let match: RegExpExecArray | null = markdownLinkRegex.exec(content);
  while (match !== null) {
    const label = match[1];
    const href = match[2];
    const pathMatch = href.match(WORKSPACE_PATH_REGEX);
    if (pathMatch) {
      const profileId = Number(pathMatch[1]);
      const tabId = Number(pathMatch[2]);
      const displayContent = normalizeContent(content.replace(match[0], label));
      return { link: { profileId, tabId }, displayContent };
    }
    match = markdownLinkRegex.exec(content);
  }

  const rawMatch = content.match(RAW_WORKSPACE_LINK_REGEX);
  if (rawMatch) {
    const pathMatch = rawMatch[0].match(WORKSPACE_PATH_REGEX);
    if (pathMatch) {
      const profileId = Number(pathMatch[1]);
      const tabId = Number(pathMatch[2]);
      const displayContent = normalizeContent(content.replace(rawMatch[0], ""));
      return { link: { profileId, tabId }, displayContent };
    }
  }

  return { link: null, displayContent: content };
};

const noop = () => {};

type ChatMessageMetaRowProps = {
  label: ReactNode;
  value: ReactNode;
  valueAs?: "span" | "code";
};

const ChatMessageMetaRow = ({ label, value, valueAs = "span" }: ChatMessageMetaRowProps) => {
  const ValueTag = valueAs;
  return (
    <div className="chat-message-meta-row">
      <span className="chat-message-meta-label">{label}</span>
      <ValueTag className="chat-message-meta-value">{value}</ValueTag>
    </div>
  );
};

type ChatDashboardCardProps = {
  profile: SlimProfile;
  tabId: number;
};

const ChatDashboardCard = ({ profile, tabId }: ChatDashboardCardProps) => {
  const { handleTabSelect } = useTabManagement();

  const handleSelect = useCallback(
    (_profileId: number, targetTabId: number) => {
      handleTabSelect(profile, targetTabId);
    },
    [handleTabSelect, profile],
  );

  return (
    <DashboardCard
      profile={profile}
      isPinned={false}
      onPin={noop}
      isExpanded={false}
      onDelete={noop}
      onEditTitle={noop}
      onTabSelect={handleSelect}
      onDuplicate={noop}
      variant="preview"
      openTabId={tabId}
    />
  );
};

export type AiChatMessageRowProps = {
  message: AiChatMessage;
  onRevert?: (messageId: string, undoIndex: number) => void;
  revertState?: "idle" | "reverting" | "done" | "error";
  canRevert?: boolean;
  revertUndoIndex?: number;
};

export const AiChatMessageRow = ({
  message,
  onRevert,
  revertState = "idle",
  canRevert = false,
  revertUndoIndex,
}: AiChatMessageRowProps) => {
  const { link, displayContent } = useMemo(() => extractWorkspaceLink(message.content), [message.content]);
  const createdDashboard = message.role === "assistant" ? message.meta?.createdDashboard : undefined;
  const createdDashboardProfile = message.role === "assistant" ? message.meta?.createdDashboardProfile : undefined;
  const cardProfileId = createdDashboard?.profileId ?? link?.profileId ?? -1;
  const linkedProfileFromStore = useProfile(cardProfileId);
  const linkedProfile = createdDashboardProfile ?? linkedProfileFromStore;
  const hasCreateDashboardAction =
    message.role === "assistant" && Array.isArray(message.meta?.actions)
      ? message.meta.actions.some((a) => a?.name === "createDashboard")
      : false;
  const shouldShowCard =
    !!linkedProfile && (!!createdDashboard || !!createdDashboardProfile || (hasCreateDashboardAction && !!link));
  const previewTabId =
    createdDashboard?.tabId ?? link?.tabId ?? linkedProfile?.tab_active ?? linkedProfile?.tabs?.[0]?.id ?? -1;

  const hasContent = displayContent.trim().length > 0;
  const [isMetaOpen, setIsMetaOpen] = useState(false);
  const meta = message.meta;
  const quotaPercent = meta?.quota?.percent_remaining;
  const hasMeta =
    message.role === "assistant" &&
    !!meta &&
    (meta.mm_api_url ||
      meta.intent ||
      typeof quotaPercent === "number" ||
      meta.tokens?.total ||
      meta.tokens?.prompt ||
      meta.tokens?.completion);
  const showRevert =
    message.role === "assistant" &&
    typeof revertUndoIndex === "number" &&
    Number.isFinite(revertUndoIndex) &&
    canRevert;
  const isReverting = revertState === "reverting";
  const isRevertDone = revertState === "done";

  return (
    <div className={message.role === "user" ? "chat-user-message" : "chat-ai-response"}>
      {hasContent && <ReactMarkdown remarkPlugins={[remarkGfm]}>{displayContent}</ReactMarkdown>}
      {shouldShowCard && linkedProfile && (
        <div className="chat-dashboard-preview">
          <ChatDashboardCard profile={linkedProfile} tabId={previewTabId} />
        </div>
      )}
      {hasMeta && isMetaOpen && (
        <div className="chat-message-meta-panel">
          {meta?.tokens && (
            <ChatMessageMetaRow
              label={t`Tokens`}
              value={
                <>
                  {meta.tokens.total}{" "}
                  <span className="chat-message-meta-sub">
                    ({t`prompt`} {meta.tokens.prompt}, {t`completion`} {meta.tokens.completion})
                  </span>
                </>
              }
            />
          )}
          {meta?.mm_api_url && <ChatMessageMetaRow label={t`MM API URL`} valueAs="code" value={meta.mm_api_url} />}
          {meta?.intent && <ChatMessageMetaRow label={t`Intent`} value={meta.intent} />}
          {meta?.createdDashboard?.profileId != null && (
            <ChatMessageMetaRow
              label={t`Created dashboard`}
              valueAs="code"
              value={
                meta.createdDashboard.tabId != null
                  ? `/workspace/${meta.createdDashboard.profileId}/${meta.createdDashboard.tabId}`
                  : String(meta.createdDashboard.profileId)
              }
            />
          )}
          {typeof quotaPercent === "number" && (
            <ChatMessageMetaRow label={t`Quota remaining`} value={`${quotaPercent}%`} />
          )}
        </div>
      )}
      {(hasMeta || showRevert) && (
        <div className="chat-message-footer">
          {hasMeta && (
            <button type="button" className="chat-message-meta-toggle" onClick={() => setIsMetaOpen((prev) => !prev)}>
              <span className="material-icons">info</span>
              {isMetaOpen ? t`Hide details` : t`Show details`}
            </button>
          )}
          {showRevert && (
            <div className="chat-revert-container">
              <button
                type="button"
                className="chat-message-meta-toggle"
                onClick={() => {
                  if (typeof revertUndoIndex === "number") {
                    onRevert?.(message.id, revertUndoIndex);
                  }
                }}
                disabled={isReverting || isRevertDone}
              >
                <span className="material-icons">undo</span>
                {isReverting ? t`Reverting…` : isRevertDone ? t`Changes reverted` : t`Revert changes`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
